import React, { useState } from 'react';
import { X, Send, HelpCircle, Bug, MessageSquare, Shield, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const HelpSupportModal = ({ isOpen, onClose, userId, userEmail, userName }) => {
  const [type, setType] = useState('question');
  const [category, setCategory] = useState('general');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('medium');
  const [submitting, setSubmitting] = useState(false);

  const ticketTypes = [
    { id: 'question', label: 'Question', icon: MessageSquare, color: 'blue' },
    { id: 'issue', label: 'Report Issue', icon: Bug, color: 'red' },
    { id: 'claim', label: 'Claim/Dispute', icon: Shield, color: 'purple' },
    { id: 'feedback', label: 'Feedback', icon: HelpCircle, color: 'green' }
  ];

  const categories = [
    { id: 'general', label: 'General' },
    { id: 'bug', label: 'Bug/Error' },
    { id: 'feature', label: 'Feature Request' },
    { id: 'account', label: 'Account Issue' },
    { id: 'billing', label: 'Billing' },
    { id: 'other', label: 'Other' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!subject.trim() || !description.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/admin/tickets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId || 'anonymous',
          user_email: userEmail,
          user_name: userName,
          type,
          category,
          subject: subject.trim(),
          description: description.trim(),
          priority
        })
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Your ticket has been submitted! We\'ll get back to you soon.');
        setSubject('');
        setDescription('');
        setType('question');
        setCategory('general');
        setPriority('medium');
        onClose();
      } else {
        throw new Error(data.detail || 'Failed to submit ticket');
      }
    } catch (error) {
      toast.error('Failed to submit ticket. Please try again.');
      console.error('Error submitting ticket:', error);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-500/20 rounded-lg">
              <HelpCircle className="text-purple-400" size={24} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Help & Support</h2>
              <p className="text-sm text-gray-400">We're here to help</p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
          >
            <X size={20} className="text-gray-400" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-5 space-y-5">
          {/* Ticket Type */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">What can we help you with?</label>
            <div className="grid grid-cols-2 gap-2">
              {ticketTypes.map(t => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setType(t.id)}
                  className={`flex items-center gap-2 p-3 rounded-lg border transition-all ${
                    type === t.id 
                      ? `border-${t.color}-500 bg-${t.color}-500/20 text-${t.color}-400`
                      : 'border-gray-700 bg-gray-800 text-gray-300 hover:border-gray-600'
                  }`}
                >
                  <t.icon size={18} />
                  <span className="text-sm font-medium">{t.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Category</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full px-4 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              {categories.map(c => (
                <option key={c.id} value={c.id}>{c.label}</option>
              ))}
            </select>
          </div>

          {/* Subject */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Subject *</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Brief summary of your request"
              className="w-full px-4 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Description *</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Please describe your issue, question, or feedback in detail..."
              rows={4}
              className="w-full px-4 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
              required
            />
          </div>

          {/* Priority (only for issues) */}
          {type === 'issue' && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Priority</label>
              <div className="flex gap-2">
                {['low', 'medium', 'high', 'critical'].map(p => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setPriority(p)}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${
                      priority === p
                        ? p === 'critical' ? 'bg-red-500 text-white'
                          : p === 'high' ? 'bg-orange-500 text-white'
                          : p === 'medium' ? 'bg-yellow-500 text-black'
                          : 'bg-green-500 text-white'
                        : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                    }`}
                  >
                    {p.charAt(0).toUpperCase() + p.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={submitting}
            className="w-full py-3 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-semibold rounded-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Send size={18} />
                Submit Ticket
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default HelpSupportModal;
